import { Light, LightFields, LightProperties } from '../../core/Light';
import { Vector3, Euler } from 'three';
import { Box } from '@react-three/drei';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { colors } from '@/constants/theme';
import { Field } from '@/types/core/Field';

export interface PointLightProperties extends LightProperties {
  intensity: number;
  color: string;
}

// Create a reusable Point Light component that can be used in both preview and world
const PointLightComponent = forwardRef<THREE.PointLight, { properties: PointLightProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { intensity, color, transform } = properties;
  const localRef = useRef<THREE.PointLight>(null);

  // Forward the ref to parent
  useImperativeHandle(ref, () => localRef.current!, []);

  // In preview mode, use simplified transform
  const position = isPreview ? [0, 2, 0] : [transform.position.x, transform.position.y, transform.position.z];

  return (
    <>
      <pointLight
        ref={localRef}
        intensity={intensity}
        color={color}
        position={position as [number, number, number]}
        castShadow
      >
        {!isPreview && (
          <mesh scale={0.2}>
            <sphereGeometry />
            <meshBasicMaterial color={color} />
          </mesh>
        )}
      </pointLight>
      {isPreview && (
        <>
          {/* Ground plane to show shadows */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]} receiveShadow>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color={colors.contrast} />
          </mesh>
          {/* Demo box to cast shadows (slightly rotated to see shadows on box)*/}
          <mesh position={[0, 0, 0]} rotation={[-Math.PI / 6, Math.PI / 6, 0]} castShadow receiveShadow>
            <boxGeometry args={[1, 1, 1]} />
            <meshStandardMaterial color={colors.primary} />
          </mesh>
        </>
      )}
    </>
  );
});

export const PointLightItem: Light<PointLightProperties> = {
  id: '',
  name: 'Point Light',
  type: 'light',
  subtype: 'point',
  icon: 'Light',
  properties: {
    intensity: 1,
    color: '#ffffff',
    transform: {
      position: new Vector3(0, 0, 0),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1),
    },
  },
  fields: [
    ...LightFields,
    {
      key: 'properties.transform.position',
      type: 'vector3',
      label: 'Position',
      default: new Vector3(),
      panel: 'transform',
    },
  ],
  render: (properties: PointLightProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <PointLightComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.PointLight>} />;
  }
};
